import { Provider, ProviderSearch, SortDirectionEnum } from '@/models/gen/graphql';
import { QueryInputType, onEnter, queryInput } from '@/utils';
import { SEARCH_PROVIDERS_PAGE_SIZE, useSearchClientsTable } from '@/api/services/providers/searchProviders';
import { useCallback, useMemo, useRef, useState } from 'react';

import { Badge } from 'react-bootstrap';
import ClientsTable from '@/features/Clients/components/ClientsTable';
import ConfirmationButton from '@/components/ConfirmationButton';
import EditClientsModal from '@/components/EditClientsModal';
import Filters from '@/components/Filters';
import FormButton from '@/components/FormButton';
import FormField from '@/components/FormField';
import HasPermission from '@/components/HasPermission';
import PageInfo from '@/components/PageInfo';
import deleteClientBulk from '@/api/services/providers/deleteClientBulk';
import { stringify } from '@/utils/objects';
import useHotkeys from '@/hooks/useHotkeys';

type ClientsPageState = {
  selected: string[];
  editing: boolean;
  creating: boolean;
  search: string;
};

const initClientsPageState: ClientsPageState = {
  selected: [],
  editing: false,
  creating: false,
  search: '',
};

const ClientsTablePage = (): JSX.Element => {
  const [state, setState] = useState(initClientsPageState);
  const { selected, editing, creating, search } = state;

  // HotKeys
  const searchbarRef = useRef(null);
  useHotkeys('clients', {
    'shift+ctrl+s': {
      name: 'Focus Search Bar',
      description: 'Focuses the Search Bar',
      action: (): any => searchbarRef.current?.focus(),
    },
    'shift+ctrl+c': {
      name: 'Toggle Create Trip',
      description: 'Opens and closes the Create Trip modal',
      action: (): any =>
        HasPermission.check('allowCreateClient')
          ? setState((current): ClientsPageState => ({ ...current, creating: !current?.creating, editing: false }))
          : undefined,
    },
    'shift+ctrl+e': {
      name: 'Toggle Edit Trip(s)',
      description: 'Opens and closes the Edit Trip modal',
      action: (): void => {
        if (selected?.length > 0) {
          setState((current): ClientsPageState => ({ ...current, creating: false, editing: !current?.editing }));
        }
      },
    },
  });

  // Init GraphApi Queries
  const [{ data, loading }, { fetch: searchClients, refetch, fetchMore }] = useSearchClientsTable();
  const { rows = [], hasNextPage = false, totalCount = 0 } = data || {};

  // State Handlers
  const onHide = useCallback(async (): Promise<void> => {
    setState((current): ClientsPageState => ({ ...current, editing: false, creating: false, selected: [] }));
    await refetch();
  }, [refetch]);

  const lastQuery = useRef(null);
  const handleSearch = async (): Promise<void> => {
    const query: ProviderSearch = {
      createdAt: queryInput([], QueryInputType.DEFAULT, SortDirectionEnum.Desc, 1),
    };
    await (stringify.compare(lastQuery.current, query) ? refetch() : searchClients([query]));
    lastQuery.current = query;
  };

  const onFetchMore = useCallback(async (): Promise<void> => {
    if (loading || !hasNextPage) return;
    await fetchMore([lastQuery.current], {
      page: Math.round(rows.length / SEARCH_PROVIDERS_PAGE_SIZE),
      merge: true,
    });
  }, [loading, hasNextPage, fetchMore, rows.length]);

  // Selection handler: toggles single ID or select/deselects all when an array is passed
  const onSelect = useCallback((id: string | string[]): void => {
    setState((current): ClientsPageState => {
      if (Array.isArray(id)) {
        const newSelected = id.length !== current.selected.length ? id : [];
        return { ...current, selected: newSelected };
      }
      const newSelected = current.selected.includes(id) ? current.selected.filter((s): boolean => s !== id) : [id, ...current.selected];
      return { ...current, selected: newSelected };
    });
  }, []);

  const onEdit = useCallback((): void => {
    setState((current): ClientsPageState => ({ ...current, editing: true, creating: false }));
  }, []);

  // Client-side search filter
  const filteredRows = useMemo((): Provider[] => {
    if (!search) return rows;
    return rows.filter((client): boolean => {
      try {
        const expression = new RegExp(search, 'gi');
        return !!stringify(client).match(expression);
      } catch {
        return true;
      }
    });
  }, [rows, search]);

  // Modal data derived from current selection
  const modalData = useMemo(
    (): Partial<Provider> | undefined =>
      editing ? (filteredRows || []).find((node: Provider): boolean => node.id === selected[0]) : creating ? { active: 0 } : undefined,
    [editing, creating, filteredRows, selected]
  );

  const modalSelectedOptions = useMemo(
    (): Provider[] => (filteredRows || []).filter((row: Provider): boolean => selected?.includes(row.id)),
    [filteredRows, selected]
  );

  return (
    <>
      <PageInfo>
        {filteredRows.length} / {totalCount || 0} Clients
      </PageInfo>
      <Filters
        name="clientsTable"
        onSubmit={handleSearch}
        onReset={(): void => setState((current): ClientsPageState => ({ ...current, search: '' }))}
        submitOnMount
        primary={({ values, onChange }): JSX.Element => {
          const { search } = values;
          return (
            <>
              <FormField
                prepend={<i className="sv sv-magnifier fs-4" />}
                name="search"
                onChange={onChange}
                onBlur={(): void => setState((current): ClientsPageState => ({ ...current, search }))}
                onKeyDown={onEnter((): void => setState((current): ClientsPageState => ({ ...current, search })))}
                placeholder="Search"
                value={search || ''}
                style={{ width: 300 }}
                condensed
              />
            </>
          );
        }}
        alternate={(): JSX.Element => {
          return (
            <>
              {/* Create Button */}
              <HasPermission name="allowCreateClient">
                <FormButton
                  icon={<i className="sv sv-plus-square {font-size:1.5rem;}" />}
                  name="CREATE_CLIENT"
                  variant="outline-gray"
                  onClick={(): void => setState((current): ClientsPageState => ({ ...current, creating: true, editing: false }))}
                >
                  <span>Add Client</span>
                </FormButton>
              </HasPermission>
              {/* Edit Bulk Button */}
              <FormButton
                icon={<i className="sv sv-layers {font-size:1.5rem;}" />}
                name="EDIT_CLIENT"
                variant="outline-gray"
                onClick={(): void => setState((current): ClientsPageState => ({ ...current, creating: false, editing: true }))}
                disabled={selected?.length === 0}
              >
                <span>Edit Client{selected.length > 1 ? 's' : ''}</span>
                {selected?.length > 1 && (
                  <Badge className="ms-2" pill bg="success">
                    {selected?.length}
                  </Badge>
                )}
              </FormButton>
              {/* Delete Button */}
              <ConfirmationButton
                icon={<i className="sv sv-trash2 {font-size:1.5rem;}" />}
                tooltip={`Delete Client${selected.length > 1 ? 's' : ''}`}
                name="DELETE_CLIENT"
                variant="outline-gray"
                disabled={!selected?.length}
                onConfirm={async (): Promise<void> => {
                  await deleteClientBulk(selected);
                  await onHide();
                }}
                options={{
                  confirmation: {
                    Body: {
                      message: `Deleting ${selected?.length} Client(s). Are you sure?`,
                    },
                  },
                }}
              />
            </>
          );
        }}
      />
      <ClientsTable
        data={filteredRows}
        selected={selected}
        onSelect={onSelect}
        onEdit={onEdit}
        fetchMore={hasNextPage && rows.length < totalCount ? onFetchMore : undefined}
      />
      <EditClientsModal
        show={!!editing || !!creating}
        data={modalData}
        onHide={onHide}
        options={{ selected: modalSelectedOptions }}
        loading={false}
        name="editClients"
      />
    </>
  );
};

export default ClientsTablePage;
